import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@etain/ui";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setCsrfToken, type JsonRecord } from "../lib/api.js";
import { normalizeSession, useSession } from "../session.js";
import { rememberMicrosoftReturn } from "../lib/auth-return-target.js";
import { ErrorState, LoadingState } from "./Page.js";

export function ReturnAfterLogin({ target, tenantId }: { target: string; tenantId?: string }) {
  const { data: session } = useSession(),
    navigate = useNavigate(),
    client = useQueryClient();
  const pending = useRef<Promise<{ destination: string; session: JsonRecord }> | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (!session) return;
    let active = true;
    setCsrfToken(session.csrfToken);
    // StrictMode attaches a second handler to the same request instead of rotating twice.
    pending.current ??= api.post("/auth/return-target", {
      target,
      ...(tenantId ? { tenantId } : {}),
    });
    void pending.current
      .then(async (result) => {
        if (!active) return;
        await client.cancelQueries({ predicate: (query) => query.queryKey[0] !== "session" });
        if (!active) return;
        client.removeQueries({ predicate: (query) => query.queryKey[0] !== "session" });
        const updated = normalizeSession(result.session);
        setCsrfToken(updated.csrfToken);
        client.setQueryData(["session"], updated);
        rememberMicrosoftReturn();
        navigate(result.destination, { replace: true });
      })
      .catch((reason: unknown) => {
        if (active) setError(reason);
      });
    return () => {
      active = false;
    };
  }, [client, navigate, session, target, tenantId]);
  return (
    <main className="boot-screen">
      {error ? (
        <>
          <ErrorState error={error} />
          <Button
            onClick={() =>
              navigate(session?.user.role === "learner" ? "/lernportal" : "/", { replace: true })
            }
          >
            Zur Übersicht
          </Button>
        </>
      ) : (
        <LoadingState cards={1} />
      )}
    </main>
  );
}
